import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Shield, Search, X, CheckSquare, Square, Save, AlertCircle } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';
const UNGROUPED  = 'General';

const PERMISSION_COLUMNS = [
  { key: 'canview',   label: 'View'   },
  { key: 'canadd',    label: 'Add'    },
  { key: 'canedit',   label: 'Edit'   },
  { key: 'candelete', label: 'Delete' },
];

const UserTypeMenuAccessMatrix = () => {
  const [userTypes,   setUserTypes]   = useState([]);
  const [menuItems,   setMenuItems]   = useState([]);
  const [accessList,  setAccessList]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [searchTerm,  setSearchTerm]  = useState('');

  const [selectedUserTypeId, setSelectedUserTypeId] = useState('');
  const [permMap, setPermMap] = useState({});
  const [dirty,   setDirty]   = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [utRes, miRes, alRes] = await Promise.all([
        apiClient.get('/UserTypes.php', { params: { active: true } }),
        apiClient.get('/MenuItems.php', { params: { active: true } }),
        apiClient.get('/UserTypeMenuAccess.php'),
      ]);
      if (utRes.data.success) setUserTypes(utRes.data.data || []);
      if (miRes.data.success) setMenuItems(miRes.data.data || []);
      if (alRes.data.success) setAccessList(alRes.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    } finally { setLoading(false); }
  };

  const buildPermMap = useCallback((userTypeId) => {
    const map = {};
    menuItems.forEach(m => {
      map[m.menuid] = { canview: false, canadd: false, canedit: false, candelete: false };
    });
    accessList
      .filter(a => parseInt(a.usertypeid) === parseInt(userTypeId))
      .forEach(a => {
        map[a.menuid] = {
          canview:   a.canview === 1,
          canadd:    a.canadd === 1,
          canedit:   a.canedit === 1,
          candelete: a.candelete === 1,
        };
      });
    return map;
  }, [menuItems, accessList]);

  const selectUserType = (id) => {
    if (dirty && !window.confirm('You have unsaved changes. Switch user type and discard them?')) return;
    setSelectedUserTypeId(id);
    setPermMap(id ? buildPermMap(id) : {});
    setDirty(false);
  };

  const toggleCell = (menuId, key) => {
    setPermMap(prev => ({
      ...prev,
      [menuId]: { ...prev[menuId], [key]: !prev[menuId][key] },
    }));
    setDirty(true);
  };

  const toggleColumn = (key) => {
    const allChecked = filteredMenus.every(m => permMap[m.menuid]?.[key]);
    setPermMap(prev => {
      const next = { ...prev };
      filteredMenus.forEach(m => {
        next[m.menuid] = { ...next[m.menuid], [key]: !allChecked };
      });
      return next;
    });
    setDirty(true);
  };

  const toggleGroupColumn = (groupName, key) => {
    const rows = groupedMenus.find(g => g.groupName === groupName)?.items || [];
    const allChecked = rows.every(m => permMap[m.menuid]?.[key]);
    setPermMap(prev => {
      const next = { ...prev };
      rows.forEach(m => {
        next[m.menuid] = { ...next[m.menuid], [key]: !allChecked };
      });
      return next;
    });
    setDirty(true);
  };

  const filteredMenus = useMemo(() => {
    if (!searchTerm.trim()) return menuItems;
    const q = searchTerm.toLowerCase();
    return menuItems.filter(m =>
      m.menuname.toLowerCase().includes(q) ||
      m.menucode.toLowerCase().includes(q) ||
      (m.parentmenuname || '').toLowerCase().includes(q)
    );
  }, [menuItems, searchTerm]);

  // Group by parentmenuname (the "menu group"); ungrouped items fall under a generic bucket
  const groupedMenus = useMemo(() => {
    const groups = {};
    filteredMenus.forEach(m => {
      const groupName = m.parentmenuname || UNGROUPED;
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(m);
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === UNGROUPED) return 1;
        if (b === UNGROUPED) return -1;
        return a.localeCompare(b);
      })
      .map(groupName => ({
        groupName,
        items: groups[groupName].sort((a, b) => a.menuname.localeCompare(b.menuname)),
      }));
  }, [filteredMenus]);

  const handleSave = async () => {
    if (!selectedUserTypeId) return;
    setSaving(true);
    try {
      const permissions = Object.entries(permMap).map(([menuid, perms]) => ({
        menuid: parseInt(menuid),
        canview: perms.canview ? 1 : 0,
        canadd: perms.canadd ? 1 : 0,
        canedit: perms.canedit ? 1 : 0,
        candelete: perms.candelete ? 1 : 0,
      }));

      const res = await apiClient.post('/UserTypeMenuAccess.php', {
        bulk: true,
        usertypeid: parseInt(selectedUserTypeId),
        permissions,
      });

      if (res.data.success) {
        alert('Permissions saved successfully!');
        setDirty(false);
        const alRes = await apiClient.get('/UserTypeMenuAccess.php');
        if (alRes.data.success) setAccessList(alRes.data.data || []);
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving permissions. Please try again.');
    } finally { setSaving(false); }
  };

  const PermIcon = ({ checked }) =>
    checked
      ? <CheckSquare size={18} className="text-emerald-500" />
      : <Square      size={18} className="text-gray-300" />;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Shield size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Menu Access Matrix</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Set permissions for every menu at once, per user type</p>
          </div>
        </div>
        {dirty && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-400/90 rounded-lg text-xs font-bold text-amber-900">
            <AlertCircle size={14} /> Unsaved changes
          </div>
        )}
      </div>

      {/* TOOLBAR */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <select value={selectedUserTypeId} onChange={e => selectUserType(e.target.value)}
          className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-bold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b] min-w-[220px]">
          <option value="">-- Select User Type --</option>
          {userTypes.map(t => <option key={t.usertypeid} value={t.usertypeid}>{t.usertypename}</option>)}
        </select>

        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Filter menus or groups..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <button onClick={handleSave} disabled={!selectedUserTypeId || !dirty || saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {/* BODY */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading...</div>
        ) : !selectedUserTypeId ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Shield size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold">Select a user type above to manage its menu access</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: '#fff' }}>
                    Menu
                  </th>
                  {PERMISSION_COLUMNS.map(col => (
                    <th key={col.key} className="px-4 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none"
                      style={{ color: '#fff' }} onClick={() => toggleColumn(col.key)} title={`Toggle ${col.label} for all rows`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedMenus.map(group => (
                  <React.Fragment key={group.groupName}>
                    {/* GROUP HEADER ROW */}
                    <tr className="bg-violet-50 border-y border-violet-100">
                      <td className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-violet-700">
                        {group.groupName}
                      </td>
                      {PERMISSION_COLUMNS.map(col => (
                        <td key={col.key} className="px-4 py-2 text-center">
                          <button type="button" onClick={() => toggleGroupColumn(group.groupName, col.key)}
                            title={`Toggle ${col.label} for all menus in ${group.groupName}`}
                            className="text-[10px] font-bold text-violet-500 uppercase cursor-pointer bg-transparent border-0 hover:text-violet-800 hover:underline">
                            all
                          </button>
                        </td>
                      ))}
                    </tr>

                    {/* MENU ROWS WITHIN GROUP */}
                    {group.items.map(m => {
                      const perms = permMap[m.menuid] || { canview: false, canadd: false, canedit: false, candelete: false };
                      return (
                        <tr key={m.menuid} className="border-b border-gray-100 hover:bg-gray-50 transition-all duration-150">
                          <td className="px-4 py-3 pl-8 font-semibold text-gray-800">
                            {m.menucode} — {m.menuname}
                          </td>
                          {PERMISSION_COLUMNS.map(col => (
                            <td key={col.key} className="px-4 py-3 text-center">
                              <button type="button" onClick={() => toggleCell(m.menuid, col.key)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border-0 bg-transparent cursor-pointer hover:bg-gray-100">
                                <PermIcon checked={perms[col.key]} />
                              </button>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTypeMenuAccessMatrix;